/** One authored side of a CSS margin, "" when the side carries no value. */
export interface MarginSides {
  top: string;
  right: string;
  bottom: string;
  left: string;
}

/** Which side of a margin a value belongs to. */
export type MarginSide = keyof MarginSides;

/** Rough pixel width of one unit, for scaling a diagram only. */
const UNIT_PIXELS: Record<string, number | undefined> = {
  "": 1,
  px: 1,
  pt: 96 / 72,
  pc: 16,
  in: 96,
  cm: 96 / 2.54,
  mm: 96 / 25.4,
  rem: 16,
  em: 16,
  ch: 8,
  ex: 8,
  "%": 2,
  vw: 8,
  vh: 8,
};

/** How far one step moves a side, per unit, so a coarse unit does not leap. */
const UNIT_STEP: Record<string, number | undefined> = {
  in: 0.05,
  cm: 0.1,
  pc: 0.25,
  rem: 0.25,
  em: 0.25,
  "%": 0.5,
  vw: 0.5,
  vh: 0.5,
};

/** A number with an optional unit, the only shape the knob can do maths on. */
const LENGTH_PATTERN = /^(-?\d*\.?\d+)([a-z%]*)$/i;

/**
 * Splits a CSS margin shorthand into its four sides, applying the shorthand's
 * own expansion rules so one, two or three stated values fill the rest.
 * @param value - The authored shorthand ("" when unset)
 * @returns The value per side, "" for a side the shorthand does not state
 */
export function parseMarginSides(value: string): MarginSides {
  const [top = "", right = top, bottom = top, left = right] =
    splitTopLevel(value);
  return { top, right, bottom, left };
}

/**
 * Writes four sides back as the shortest shorthand that means the same thing,
 * with an unstated side standing in as 0.
 * @param sides - The value per side
 * @returns The shorthand, or "" when no side carries a value
 */
export function serializeMarginSides(sides: MarginSides): string {
  const stated = [sides.top, sides.right, sides.bottom, sides.left].map(
    (side) => side.trim(),
  );
  if (stated.every((side) => side === "")) {
    return "";
  }
  const [top, right, bottom, left] = stated.map((side) =>
    side === "" ? "0" : side,
  );
  if (top === right && right === bottom && bottom === left) {
    return top;
  }
  if (top === bottom && right === left) {
    return `${top} ${right}`;
  }
  if (right === left) {
    return `${top} ${right} ${bottom}`;
  }
  return `${top} ${right} ${bottom} ${left}`;
}

/**
 * Steps the number in a CSS length by the unit's own step, leaving the unit
 * alone and a keyword whole.
 * @param length - The current side value
 * @param steps - How many steps to move, negative to shrink
 * @returns The stepped length, or the value unchanged when it holds no number
 */
export function steppedLength(length: string, steps: number): string {
  const parts = LENGTH_PATTERN.exec(length.trim());
  if (parts === null) {
    return length;
  }
  const step = UNIT_STEP[parts[2].toLowerCase()] ?? 1;
  const stepped = Number(parts[1]) + steps * step;
  return `${Math.round(stepped * 1000) / 1000}${parts[2]}`;
}

/**
 * Approximates a side's width in CSS pixels, for scaling a diagram only.
 * @param side - The authored side value
 * @returns The approximate pixels, 0 for a keyword or an unknown unit
 */
export function approximatePixels(side: string): number {
  const parts = LENGTH_PATTERN.exec(side.trim());
  if (parts === null) {
    return 0;
  }
  const unit = UNIT_PIXELS[parts[2].toLowerCase()];
  return unit === undefined ? 0 : Number(parts[1]) * unit;
}

/**
 * Splits on whitespace outside brackets, so a `calc(1px + 2px)` side survives
 * as one token rather than being torn into three.
 * @param value - The authored shorthand
 * @returns The stated values, in order
 */
function splitTopLevel(value: string): string[] {
  const tokens: string[] = [];
  let depth = 0;
  let current = "";
  for (const character of value.trim()) {
    if (character === "(") {
      depth += 1;
    }
    if (character === ")") {
      depth = Math.max(0, depth - 1);
    }
    if (depth === 0 && /\s/.test(character)) {
      if (current !== "") {
        tokens.push(current);
        current = "";
      }
      continue;
    }
    current += character;
  }
  if (current !== "") {
    tokens.push(current);
  }
  return tokens;
}
